import React, { useEffect, useMemo, useState } from "react";
import CalendarPane from "../calendar/components/CalendarPane";
import GuestsDropdown from "./components/GuestsDropdown";
import LocationDropdown from "./components/LocationDropdown";
import FilterModal from "./components/FilterModal";
import { FilterIcon, SearchButtonIcon } from "./components/icons";
import { getHostEl, getCalendarHostEl } from "./utils/hostElements";
import { normalizeFF, parseMaybe, normalizeFAFamily, readDataAttr } from "./utils/helpers";
import {
  injectWidgetStyles,
  ensureSearchWidgetResponsiveCSS,
  ensureCalendarRuntimeCSS,
  ensureFontAwesome,
  ensureSearchCalendarInputCSS
} from "./utils/cssInjector";
import { logger } from "../shared/logger";
// Note: We DON'T use getCachedAccount - search widget gets styles from data attributes only

/* Read all style settings from data attributes */
function readStyleFromDataAttrs(el) {
  if (!el) return null;

  // Check if any data attributes exist
  const hasDataAttrs = el.hasAttribute("data-container-padding") ||
                       el.hasAttribute("data-pill-font-size") ||
                       el.hasAttribute("data-filter-font-size");

  if (!hasDataAttrs) return null;

  return {
    widgetContainer: {
      padding: readDataAttr(el, "data-container-padding", undefined),
      borderRadius: readDataAttr(el, "data-container-radius", undefined),
      borderColor: readDataAttr(el, "data-container-border-color", undefined),
      bgColor: readDataAttr(el, "data-container-bg-color", undefined),
      buttonOrder: readDataAttr(el, "data-button-order", "")?.split(",").filter(Boolean),
    },
    pill: {
      fontSize: readDataAttr(el, "data-pill-font-size", undefined),
      fontWeight: readDataAttr(el, "data-pill-font-weight", undefined),
      fontFamily: readDataAttr(el, "data-pill-font-family", undefined),
      fontColor: readDataAttr(el, "data-pill-font-color", undefined),
      borderColor: readDataAttr(el, "data-pill-border-color", undefined),
      bgColor: readDataAttr(el, "data-pill-bg-color", undefined),
      borderRadius: readDataAttr(el, "data-pill-border-radius", undefined),
      paddingX: readDataAttr(el, "data-pill-padding-x", undefined),
      paddingY: readDataAttr(el, "data-pill-padding-y", undefined),
      gap: readDataAttr(el, "data-pill-gap", undefined),
      minWidth: readDataAttr(el, "data-pill-min-width", undefined),
      minWidthUnit: readDataAttr(el, "data-pill-min-width-unit", undefined),
      guestLabelSingular: readDataAttr(el, "data-guests-label-singular", undefined),
      guestLabelPlural: readDataAttr(el, "data-guests-label-plural", undefined),
      icon: {
        mode: readDataAttr(el, "data-pill-icon-mode", undefined),
        faFamily: readDataAttr(el, "data-pill-icon-family", undefined),
        faIcon: readDataAttr(el, "data-pill-icon-name", undefined),
        size: readDataAttr(el, "data-pill-icon-size", undefined),
        color: readDataAttr(el, "data-pill-icon-color", undefined),
        customSvg: readDataAttr(el, "data-pill-icon-svg", undefined),
      },
    },
    location: {
      label: readDataAttr(el, "data-location-label", undefined),
      icon: {
        mode: readDataAttr(el, "data-location-icon-mode", undefined),
        faFamily: readDataAttr(el, "data-location-icon-family", undefined),
        faIcon: readDataAttr(el, "data-location-icon-name", undefined),
        size: readDataAttr(el, "data-location-icon-size", undefined),
        color: readDataAttr(el, "data-location-icon-color", undefined),
        customSvg: readDataAttr(el, "data-location-icon-svg", undefined),
        show: readDataAttr(el, "data-location-icon-show", undefined),
      },
    },
    filter: {
      label: readDataAttr(el, "data-filter-label", undefined),
      fontSize: readDataAttr(el, "data-filter-font-size", undefined),
      fontWeight: readDataAttr(el, "data-filter-font-weight", undefined),
      fontFamily: readDataAttr(el, "data-filter-font-family", undefined),
      fontColor: readDataAttr(el, "data-filter-font-color", undefined),
      borderColor: readDataAttr(el, "data-filter-border-color", undefined),
      bgColor: readDataAttr(el, "data-filter-bg-color", undefined),
      borderRadius: readDataAttr(el, "data-filter-border-radius", undefined),
      paddingX: readDataAttr(el, "data-filter-padding-x", undefined),
      paddingY: readDataAttr(el, "data-filter-padding-y", undefined),
      gap: readDataAttr(el, "data-filter-gap", undefined),
      minWidth: readDataAttr(el, "data-filter-min-width", undefined),
      minWidthUnit: readDataAttr(el, "data-filter-min-width-unit", undefined),
      icon: {
        mode: readDataAttr(el, "data-filter-icon-mode", undefined),
        faFamily: readDataAttr(el, "data-filter-icon-family", undefined),
        faIcon: readDataAttr(el, "data-filter-icon-name", undefined),
        size: readDataAttr(el, "data-filter-icon-size", undefined),
        color: readDataAttr(el, "data-filter-icon-color", undefined),
        customSvg: readDataAttr(el, "data-filter-icon-svg", undefined),
      },
    },
    calendarBtn: {
      placeholder: readDataAttr(el, "data-calendar-placeholder", undefined),
      fontSize: readDataAttr(el, "data-cal-font-size", undefined),
      fontWeight: readDataAttr(el, "data-cal-font-weight", undefined),
      fontFamily: readDataAttr(el, "data-cal-font-family", undefined),
      fontColor: readDataAttr(el, "data-cal-font-color", undefined),
      borderColor: readDataAttr(el, "data-cal-border-color", undefined),
      bgColor: readDataAttr(el, "data-cal-bg-color", undefined),
      borderRadius: readDataAttr(el, "data-cal-radius", undefined),
      paddingX: readDataAttr(el, "data-cal-padding-x", undefined),
      paddingY: readDataAttr(el, "data-cal-padding-y", undefined),
      minWidth: readDataAttr(el, "data-cal-min-width", undefined),
      minWidthUnit: readDataAttr(el, "data-cal-min-width-unit", undefined),
      placeholderColor: readDataAttr(el, "data-cal-placeholder-color", undefined),
      icon: {
        mode: readDataAttr(el, "data-calendar-icon-mode", undefined),
        faFamily: readDataAttr(el, "data-calendar-icon-family", undefined),
        faIcon: readDataAttr(el, "data-calendar-icon-name", undefined),
        size: readDataAttr(el, "data-calendar-icon-size", undefined),
        color: readDataAttr(el, "data-calendar-icon-color", undefined),
        customSvg: readDataAttr(el, "data-calendar-icon-svg", undefined),
        show: readDataAttr(el, "data-calendar-icon-show", undefined),
      },
    },
    searchButton: {
      label: readDataAttr(el, "data-search-button-label", undefined),
      fontSize: readDataAttr(el, "data-search-button-font-size", undefined),
      fontWeight: readDataAttr(el, "data-search-button-font-weight", undefined),
      fontFamily: readDataAttr(el, "data-search-button-font-family", undefined),
      fontColor: readDataAttr(el, "data-search-button-font-color", undefined),
      borderColor: readDataAttr(el, "data-search-button-border-color", undefined),
      bgColor: readDataAttr(el, "data-search-button-bg-color", undefined),
      borderRadius: readDataAttr(el, "data-search-button-border-radius", undefined),
      paddingX: readDataAttr(el, "data-search-button-padding-x", undefined),
      paddingY: readDataAttr(el, "data-search-button-padding-y", undefined),
      minWidth: readDataAttr(el, "data-search-button-min-width", undefined),
      minWidthUnit: readDataAttr(el, "data-search-button-min-width-unit", undefined),
    },
    panel: {
      bgColor: readDataAttr(el, "data-panel-bg-color", undefined),
      borderColor: readDataAttr(el, "data-panel-border-color", undefined),
      borderRadius: readDataAttr(el, "data-panel-border-radius", undefined),
      shadow: readDataAttr(el, "data-panel-shadow", undefined),
      padding: readDataAttr(el, "data-panel-padding", undefined),
      width: readDataAttr(el, "data-panel-width", undefined),
    },
    row: {
      gap: readDataAttr(el, "data-row-gap", undefined),
      borderColor: readDataAttr(el, "data-row-border-color", undefined),
      paddingY: readDataAttr(el, "data-row-padding-y", undefined),
      label: {
        fontSize: readDataAttr(el, "data-row-label-font-size", undefined),
        fontWeight: readDataAttr(el, "data-row-label-font-weight", undefined),
        fontFamily: readDataAttr(el, "data-row-label-font-family", undefined),
        fontColor: readDataAttr(el, "data-row-label-font-color", undefined),
      },
      sub: {
        fontSize: readDataAttr(el, "data-row-sub-font-size", undefined),
        fontWeight: readDataAttr(el, "data-row-sub-font-weight", undefined),
        fontFamily: readDataAttr(el, "data-row-sub-font-family", undefined),
        fontColor: readDataAttr(el, "data-row-sub-font-color", undefined),
      },
      value: {
        fontSize: readDataAttr(el, "data-row-value-font-size", undefined),
        fontWeight: readDataAttr(el, "data-row-value-font-weight", undefined),
        fontFamily: readDataAttr(el, "data-row-value-font-family", undefined),
        fontColor: readDataAttr(el, "data-row-value-font-color", undefined),
        minWidth: readDataAttr(el, "data-row-value-min-width", undefined),
      },
    },
    counter: {
      btnSize: readDataAttr(el, "data-counter-btn-size", undefined),
      btnRadius: readDataAttr(el, "data-counter-btn-radius", undefined),
      btnBgColor: readDataAttr(el, "data-counter-btn-bg-color", undefined),
      btnBorderColor: readDataAttr(el, "data-counter-btn-border-color", undefined),
      btnColor: readDataAttr(el, "data-counter-btn-color", undefined),
      fontSize: readDataAttr(el, "data-counter-font-size", undefined),
      fontWeight: readDataAttr(el, "data-counter-font-weight", undefined),
    },
  };
}

/* Merge data attributes with form object (data attrs take priority) */
function mergeStyleSettings(dataAttrs, form) {
  if (!dataAttrs) return form || {};
  if (!form) return dataAttrs;

  // Deep merge - data attributes override form values
  const merged = { ...form };

  Object.keys(dataAttrs).forEach(key => {
    if (dataAttrs[key] && typeof dataAttrs[key] === "object" && !Array.isArray(dataAttrs[key])) {
      merged[key] = { ...merged[key], ...dataAttrs[key] };

      // Handle nested objects (like icon, label, sub, value)
      Object.keys(dataAttrs[key]).forEach(subKey => {
        if (dataAttrs[key][subKey] && typeof dataAttrs[key][subKey] === "object") {
          merged[key][subKey] = { ...(merged[key]?.[subKey] || {}), ...dataAttrs[key][subKey] };
        }
      });
    } else if (dataAttrs[key] !== undefined) {
      merged[key] = dataAttrs[key];
    }
  });

  return merged;
}

/* Apply the same CSS variables and data-attrs as the editor layer (SEARCH) */
function applySearchStyleVarsTo(el, form) {
  if (!el) return;

  const st = el.style;

  // ============ CHECK FOR NEW GLOBAL FORMAT FIRST ============
  // If new global data attributes exist, use them directly
  const hasNewFormat = el.hasAttribute("data-primary-color") || el.hasAttribute("data-bg-light");

  if (hasNewFormat) {
    // Read new global tokens
    const g = {
      primaryColor: el.getAttribute("data-primary-color") || "#0c272b",
      textPrimary: el.getAttribute("data-text-primary") || "#222222",
      textSecondary: el.getAttribute("data-text-secondary") || "#222222",
      borderColor: el.getAttribute("data-border-color") || "#cccccc",
      borderHover: el.getAttribute("data-border-hover") || "#999999",
      bgLight: el.getAttribute("data-bg-light") || "#ffffff",
      bgWidget: el.getAttribute("data-bg-widget") || "#ffffff",
      widgetBorder: el.getAttribute("data-widget-border") !== "false",
      widgetPadding: parseInt(el.getAttribute("data-widget-padding")) || 8,
      fontFamily: el.getAttribute("data-font-family") || "inherit",
      headingSize: parseInt(el.getAttribute("data-heading-size")) || 14,
      headingWeight: el.getAttribute("data-heading-weight") || "500",
      bodySize: parseInt(el.getAttribute("data-body-size")) || 13,
      bodyWeight: el.getAttribute("data-body-weight") || "400",
      buttonWeight: el.getAttribute("data-button-weight") || "500",
      radiusSmall: parseInt(el.getAttribute("data-radius-small")) || 8,
      radiusMedium: parseInt(el.getAttribute("data-radius-medium")) || 10,
      radiusLarge: parseInt(el.getAttribute("data-radius-large")) || 12,
      paddingSmall: parseInt(el.getAttribute("data-padding-small")) || 8,
      paddingMedium: parseInt(el.getAttribute("data-padding-medium")) || 12,
      paddingLarge: parseInt(el.getAttribute("data-padding-large")) || 16,
      gap: parseInt(el.getAttribute("data-gap")) || 8,
      inputHeight: parseInt(el.getAttribute("data-input-height")) || 44,
      buttonHeight: parseInt(el.getAttribute("data-button-height")) || 44,
      iconSize: parseInt(el.getAttribute("data-icon-size")) || 16,
      iconColor: el.getAttribute("data-icon-color") || "#222222",
    };
    const panelWidth = parseInt(el.getAttribute("data-panel-width")) || 300;
    const modalWidth = parseInt(el.getAttribute("data-modal-width")) || 520;
    const panelShadow = el.getAttribute("data-panel-shadow") || "0 8px 24px rgba(2,6,23,.12)";
    const modalOverlay = el.getAttribute("data-modal-overlay") || "rgba(0,0,0,.35)";
    const inputMinWidth = parseInt(el.getAttribute("data-input-min-width")) || 150;

    // Container
    st.setProperty("--sw-container-bg", g.bgWidget);
    st.setProperty("--sw-container-bc", g.widgetBorder ? g.borderColor : "transparent");
    st.setProperty("--sw-container-r", `${g.radiusLarge}px`);
    st.setProperty("--sw-container-pad", `${g.widgetPadding}px`);

    // Pills (inputs)
    st.setProperty("--sw-pill-bg", g.bgLight);
    st.setProperty("--sw-pill-bc", g.borderColor);
    st.setProperty("--sw-pill-bc-hover", g.borderHover);
    st.setProperty("--sw-pill-fc", g.textPrimary);
    st.setProperty("--sw-pill-fs", `${g.headingSize}px`);
    st.setProperty("--sw-pill-fw", g.headingWeight);
    st.setProperty("--sw-pill-ff", g.fontFamily);
    st.setProperty("--sw-pill-r", `${g.radiusMedium}px`);
    st.setProperty("--sw-pill-px", `${g.paddingMedium}px`);
    st.setProperty("--sw-pill-py", `${g.paddingSmall}px`);
    st.setProperty("--sw-pill-gap", `${g.gap}px`);
    st.setProperty("--sw-pill-minw", `${inputMinWidth}px`);
    st.setProperty("--sw-pill-h", `${g.inputHeight}px`);

    // Filter button
    st.setProperty("--sw-filter-bg", g.bgLight);
    st.setProperty("--sw-filter-bc", g.borderColor);
    st.setProperty("--sw-filter-fc", g.textPrimary);
    st.setProperty("--sw-filter-fs", `${g.headingSize}px`);
    st.setProperty("--sw-filter-fw", g.headingWeight);
    st.setProperty("--sw-filter-ff", g.fontFamily);
    st.setProperty("--sw-filter-r", `${g.radiusMedium}px`);
    st.setProperty("--sw-filter-px", `${g.paddingMedium}px`);
    st.setProperty("--sw-filter-py", `${g.paddingSmall}px`);
    st.setProperty("--sw-filter-h", `${g.buttonHeight}px`);

    // Search button
    st.setProperty("--sw-searchbtn-bg", g.primaryColor);
    st.setProperty("--sw-searchbtn-bc", "transparent");
    st.setProperty("--sw-searchbtn-fc", "#ffffff");
    st.setProperty("--sw-searchbtn-fs", `${g.headingSize}px`);
    st.setProperty("--sw-searchbtn-fw", g.buttonWeight);
    st.setProperty("--sw-searchbtn-ff", g.fontFamily);
    st.setProperty("--sw-searchbtn-r", `${g.radiusMedium}px`);
    st.setProperty("--sw-searchbtn-px", `${g.paddingLarge}px`);
    st.setProperty("--sw-searchbtn-py", `${g.paddingSmall}px`);
    st.setProperty("--sw-searchbtn-h", `${g.buttonHeight}px`);

    // Calendar
    st.setProperty("--sw-cal-bg", g.bgLight);
    st.setProperty("--sw-cal-bc", g.borderColor);
    st.setProperty("--sw-cal-fc", g.textPrimary);
    st.setProperty("--sw-cal-pc", g.textSecondary);
    st.setProperty("--sw-cal-fs", `${g.headingSize}px`);
    st.setProperty("--sw-cal-fw", g.headingWeight);
    st.setProperty("--sw-cal-ff", g.fontFamily);
    st.setProperty("--sw-cal-r", `${g.radiusMedium}px`);
    st.setProperty("--sw-cal-px", `${g.paddingMedium}px`);
    st.setProperty("--sw-cal-py", `${g.paddingSmall}px`);
    st.setProperty("--sw-cal-h", `${g.inputHeight}px`);

    // Panel
    st.setProperty("--sw-panel-bg", g.bgLight);
    st.setProperty("--sw-panel-bc", g.borderColor);
    st.setProperty("--sw-panel-r", `${g.radiusLarge}px`);
    st.setProperty("--sw-panel-shadow", panelShadow);
    st.setProperty("--sw-panel-pad", `${g.paddingMedium}px`);
    st.setProperty("--sw-panel-w", `${panelWidth}px`);

    // Row items
    st.setProperty("--sw-row-gap", `${g.gap}px`);
    st.setProperty("--sw-row-bc", g.borderColor);
    st.setProperty("--sw-label-fs", `${g.headingSize}px`);
    st.setProperty("--sw-label-fw", g.headingWeight);
    st.setProperty("--sw-label-ff", g.fontFamily);
    st.setProperty("--sw-label-fc", g.textPrimary);
    st.setProperty("--sw-sub-fs", `${g.bodySize}px`);
    st.setProperty("--sw-sub-fw", g.bodyWeight);
    st.setProperty("--sw-sub-ff", g.fontFamily);
    st.setProperty("--sw-sub-fc", g.textSecondary);

    // Counter
    st.setProperty("--sw-ctr-size", "32px");
    st.setProperty("--sw-ctr-radius", `${g.radiusSmall}px`);
    st.setProperty("--sw-ctr-bc", g.borderColor);
    st.setProperty("--sw-ctr-bg", "#ffffff");
    st.setProperty("--sw-ctr-fc", g.textPrimary);

    // Actions
    st.setProperty("--sw-act-close-bg", "transparent");
    st.setProperty("--sw-act-close-bc", g.borderColor);
    st.setProperty("--sw-act-close-fc", g.textPrimary);
    st.setProperty("--sw-act-close-r", `${g.radiusMedium}px`);
    st.setProperty("--sw-act-apply-bg", g.primaryColor);
    st.setProperty("--sw-act-apply-bc", "transparent");
    st.setProperty("--sw-act-apply-fc", "#ffffff");
    st.setProperty("--sw-act-apply-r", `${g.radiusMedium}px`);

    // Modal
    st.setProperty("--swm-overlay", modalOverlay);
    st.setProperty("--swm-bg", g.bgLight);
    st.setProperty("--swm-bc", g.borderColor);
    st.setProperty("--swm-r", `${g.radiusLarge}px`);
    st.setProperty("--swm-w", `${modalWidth}px`);
    st.setProperty("--swm-h-fs", `${g.headingSize + 4}px`);
    st.setProperty("--swm-h-fw", "700");
    st.setProperty("--swm-h-ff", g.fontFamily);
    st.setProperty("--swm-h-fc", g.textPrimary);
    st.setProperty("--swm-cancel-bg", "transparent");
    st.setProperty("--swm-cancel-bc", g.borderColor);
    st.setProperty("--swm-cancel-fc", g.textPrimary);
    st.setProperty("--swm-apply-bg", g.primaryColor);
    st.setProperty("--swm-apply-bc", "transparent");
    st.setProperty("--swm-apply-fc", "#ffffff");

    // Icons
    st.setProperty("--sw-icon-size", `${g.iconSize}px`);
    st.setProperty("--sw-icon-color", g.iconColor);

    return; // Done - don't run old format code
  }

  // ============ OLD FORMAT FALLBACK ============
  // Read data attributes first, then merge with form
  const dataAttrs = readStyleFromDataAttrs(el);
  const mergedForm = mergeStyleSettings(dataAttrs, form);

  if (!mergedForm) return;

  // ---- widget container
  const wc = mergedForm.widgetContainer || {};
  st.setProperty("--sw-container-pad", `${wc.padding ?? 12}px`);
  st.setProperty("--sw-container-r", `${wc.borderRadius ?? 12}px`);
  st.setProperty("--sw-container-bc", wc.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-container-bg", wc.bgColor || "transparent");

  // ---- pill
  const p = mergedForm.pill || {};
  el.setAttribute("data-guest-icon-mode", p.icon?.mode || "fa");
  el.setAttribute("data-guest-icon-family", p.icon?.faFamily || "fa-solid");
  el.setAttribute("data-guest-icon-name", p.icon?.faIcon || "fa-user");
  el.setAttribute("data-guest-icon-size", String(p.icon?.size ?? 16));
  el.setAttribute("data-guest-icon-color", p.icon?.color || "#0f172a");
  if (p.icon?.customSvg) el.setAttribute("data-guest-icon-svg", p.icon.customSvg);
  else el.removeAttribute("data-guest-icon-svg");

  st.setProperty("--sw-pill-fs", `${p.fontSize ?? 14}px`);
  st.setProperty("--sw-pill-fw", p.fontWeight || "600");
  st.setProperty("--sw-pill-ff", normalizeFF(p.fontFamily || "inherit"));
  st.setProperty("--sw-pill-fc", p.fontColor || "#0f172a");
  st.setProperty("--sw-pill-bc", p.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-pill-bg", p.bgColor || "transparent");
  st.setProperty("--sw-pill-r", `${p.borderRadius ?? 10}px`);
  st.setProperty("--sw-pill-px", `${p.paddingX ?? 12}px`);
  st.setProperty("--sw-pill-py", `${p.paddingY ?? 10}px`);
  st.setProperty("--sw-pill-gap", `${p.gap ?? 8}px`);
  st.setProperty("--sw-pill-minw", `${p.minWidth ?? 100}${p.minWidthUnit || "px"}`);

  // Set guest label data attributes
  el.setAttribute("data-guests-label-singular", p.guestLabelSingular || "Guest");
  el.setAttribute("data-guests-label-plural", p.guestLabelPlural || "Guests");

  // ---- LOCATION icon (separate from pill/guests)
  const loc = mergedForm.location || {};
  el.setAttribute("data-location-label", loc.label || "Location");
  el.setAttribute("data-location-icon-mode", loc.icon?.mode || "fa");
  el.setAttribute("data-location-icon-family", loc.icon?.faFamily || "fa-solid");
  el.setAttribute("data-location-icon-name", loc.icon?.faIcon || "fa-location-dot");
  el.setAttribute("data-location-icon-size", String(loc.icon?.size ?? 16));
  el.setAttribute("data-location-icon-color", loc.icon?.color || "#0f172a");
  if (loc.icon?.customSvg) el.setAttribute("data-location-icon-svg", loc.icon.customSvg);
  else el.removeAttribute("data-location-icon-svg");
  el.setAttribute("data-location-icon-show", loc.icon?.show !== false ? "true" : "false");

  // ---- FILTER button
  const ft = mergedForm.filter || {};
  el.setAttribute("data-filter-label", ft.label || "Filter");
  el.setAttribute("data-filter-icon-mode", ft.icon?.mode || "fa");
  el.setAttribute("data-filter-icon-family", ft.icon?.faFamily || "fa-solid");
  el.setAttribute("data-filter-icon-name", ft.icon?.faIcon || "fa-sliders");
  el.setAttribute("data-filter-icon-size", String(ft.icon?.size ?? 16));
  el.setAttribute("data-filter-icon-color", ft.icon?.color || "#0f172a");
  if (ft.icon?.customSvg) el.setAttribute("data-filter-icon-svg", ft.icon.customSvg);
  else el.removeAttribute("data-filter-icon-svg");

  st.setProperty("--sw-filter-fs", `${ft.fontSize ?? 14}px`);
  st.setProperty("--sw-filter-fw", ft.fontWeight || "600");
  st.setProperty("--sw-filter-ff", normalizeFF(ft.fontFamily || "inherit"));
  st.setProperty("--sw-filter-fc", ft.fontColor || "#0f172a");
  st.setProperty("--sw-filter-bc", ft.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-filter-bg", ft.bgColor || "transparent");
  st.setProperty("--sw-filter-r", `${ft.borderRadius ?? 10}px`);
  st.setProperty("--sw-filter-px", `${ft.paddingX ?? 12}px`);
  st.setProperty("--sw-filter-py", `${ft.paddingY ?? 10}px`);
  st.setProperty("--sw-filter-gap", `${ft.gap ?? 8}px`);
  st.setProperty("--sw-filter-minw", `${ft.minWidth ?? 80}${ft.minWidthUnit || "px"}`);

  // ---- CALENDAR/DATES button (separate from pills - has its own settings in Input Fields > Calendar Button)
  const calBtn = mergedForm.calendarBtn || {};
  el.setAttribute("data-calendar-placeholder", calBtn.placeholder || "Dates");
  el.setAttribute("data-calendar-icon-mode", calBtn.icon?.mode || "fa");
  el.setAttribute("data-calendar-icon-family", calBtn.icon?.faFamily || "fa-solid");
  el.setAttribute("data-calendar-icon-name", calBtn.icon?.faIcon || "fa-calendar");
  el.setAttribute("data-calendar-icon-size", String(calBtn.icon?.size ?? 16));
  el.setAttribute("data-calendar-icon-color", calBtn.icon?.color || "#0f172a");
  el.setAttribute("data-calendar-icon-show", calBtn.icon?.show !== false ? "true" : "false");
  if (calBtn.icon?.customSvg) el.setAttribute("data-calendar-icon-svg", calBtn.icon.customSvg);
  else el.removeAttribute("data-calendar-icon-svg");

  // Set all calendar button CSS variables (controlled by Input Fields > Calendar Button section)
  st.setProperty("--sw-cal-fs", `${calBtn.fontSize ?? 14}px`);
  st.setProperty("--sw-cal-fw", calBtn.fontWeight || "500");
  st.setProperty("--sw-cal-ff", normalizeFF(calBtn.fontFamily || "inherit"));
  st.setProperty("--sw-cal-fc", calBtn.fontColor || "#0f172a");
  st.setProperty("--sw-cal-bc", calBtn.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-cal-bg", calBtn.bgColor || "transparent");
  st.setProperty("--sw-cal-r", `${calBtn.borderRadius ?? 10}px`);
  st.setProperty("--sw-cal-px", `${calBtn.paddingX ?? 12}px`);
  st.setProperty("--sw-cal-py", `${calBtn.paddingY ?? 10}px`);
  st.setProperty("--sw-cal-minw", `${calBtn.minWidth ?? 100}${calBtn.minWidthUnit || "px"}`);
  st.setProperty("--sw-cal-placeholder", calBtn.placeholderColor || "#9ca3af");
  document.documentElement.style.setProperty("--sw-cal-placeholder", calBtn.placeholderColor || "#9ca3af");

  // ---- SEARCH button
  const sb = mergedForm.searchButton || {};
  el.setAttribute("data-search-button-label", sb.label || "Search");
  st.setProperty("--sw-searchbtn-fs", `${sb.fontSize ?? 14}px`);
  st.setProperty("--sw-searchbtn-fw", sb.fontWeight || "600");
  st.setProperty("--sw-searchbtn-ff", normalizeFF(sb.fontFamily || "inherit"));
  st.setProperty("--sw-searchbtn-fc", sb.fontColor || "#ffffff");
  st.setProperty("--sw-searchbtn-bc", sb.borderColor || "transparent");
  st.setProperty("--sw-searchbtn-bg", sb.bgColor || "#f38b4a");
  st.setProperty("--sw-searchbtn-r", `${sb.borderRadius ?? 10}px`);
  st.setProperty("--sw-searchbtn-px", `${sb.paddingX ?? 16}px`);
  st.setProperty("--sw-searchbtn-py", `${sb.paddingY ?? 10}px`);
  st.setProperty("--sw-searchbtn-minw", `${sb.minWidth ?? 90}${sb.minWidthUnit || "px"}`);

  // ---- panel
  const pa = mergedForm.panel || {};
  st.setProperty("--sw-panel-bg", pa.bgColor || "#ffffff");
  st.setProperty("--sw-panel-bc", pa.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-panel-r", `${pa.borderRadius ?? 12}px`);
  st.setProperty("--sw-panel-shadow", pa.shadow || "0 8px 24px rgba(2,6,23,.12)");
  st.setProperty("--sw-panel-pad", `${pa.padding ?? 12}px`);
  st.setProperty("--sw-panel-w", `${pa.width ?? 300}px`);

  // ---- rows
  const r = mergedForm.row || {};
  st.setProperty("--sw-row-gap", `${r.gap ?? 8}px`);
  st.setProperty("--sw-row-bc", r.borderColor || "rgba(148,163,184,.2)");
  st.setProperty("--sw-row-py", `${r.paddingY ?? 10}px`);

  const rl = r.label || {};
  st.setProperty("--sw-label-fs", `${rl.fontSize ?? 14}px`);
  st.setProperty("--sw-label-fw", rl.fontWeight || "700");
  st.setProperty("--sw-label-ff", normalizeFF(rl.fontFamily || "inherit"));
  st.setProperty("--sw-label-fc", rl.fontColor || "#0f172a");

  const rs = r.sub || {};
  st.setProperty("--sw-sub-fs", `${rs.fontSize ?? 12}px`);
  st.setProperty("--sw-sub-fw", rs.fontWeight || "400");
  st.setProperty("--sw-sub-ff", normalizeFF(rs.fontFamily || "inherit"));
  st.setProperty("--sw-sub-fc", rs.fontColor || "#0f172aB3");

  const rv = r.value || {};
  st.setProperty("--sw-value-fs", `${rv.fontSize ?? 14}px`);
  st.setProperty("--sw-value-fw", `${rv.fontWeight || "700"}`);
  st.setProperty("--sw-value-ff", normalizeFF(rv.fontFamily || "inherit"));
  st.setProperty("--sw-value-fc", rv.fontColor || "#0f172a");
  st.setProperty("--sw-value-minw", `${rv.minWidth ?? 20}px`);

  // ---- counter
  const c = mergedForm.counter || {};
  st.setProperty("--sw-ctr-size", `${c.btnSize ?? 32}px`);
  st.setProperty("--sw-ctr-radius", `${c.btnRadius ?? 8}px`);
  st.setProperty("--sw-ctr-bc", c.btnBorderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-ctr-bg", c.btnBg || "#ffffff");
  st.setProperty("--sw-ctr-bg-disabled", c.btnBgDisabled || "rgba(127,127,127,.2)");
  st.setProperty("--sw-ctr-fc", c.btnTextColor || "#0f172a");

  // ---- notice
  const n = form.notice || {};
  st.setProperty("--sw-note-fs", `${n.fontSize ?? 12}px`);
  st.setProperty("--sw-note-fw", n.fontWeight || "400");
  st.setProperty("--sw-note-ff", normalizeFF(n.fontFamily || "inherit"));
  st.setProperty("--sw-note-fc", n.fontColor || "#0f172aBF");
  st.setProperty("--sw-note-mt", `${n.marginTop ?? 8}px`);

  // ---- action (legacy)
  const a = form.action || {};
  st.setProperty("--sw-act-fs", `${a.fontSize ?? 14}px`);
  st.setProperty("--sw-act-fw", a.fontWeight || "600");
  st.setProperty("--sw-act-ff", normalizeFF(a.fontFamily || "inherit"));
  st.setProperty("--sw-act-bg", a.bgColor || "transparent");
  st.setProperty("--sw-act-fc", a.textColor || "#0f172a");
  st.setProperty("--sw-act-bc", a.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-act-px", `${a.paddingX ?? 12}px`);
  st.setProperty("--sw-act-py", `${a.paddingY ?? 10}px`);
  st.setProperty("--sw-act-r", `${a.borderRadius ?? 10}px`);
  st.setProperty("--sw-act-mt", `${a.marginTop ?? 8}px`);
  el.setAttribute("data-action-fullwidth", a.fullWidth ? "true" : "false");

  // ---- actionClose (new)
  const ac = form.actionClose || {};
  st.setProperty("--sw-act-close-fs", `${ac.fontSize ?? 14}px`);
  st.setProperty("--sw-act-close-fw", ac.fontWeight || "600");
  st.setProperty("--sw-act-close-ff", normalizeFF(ac.fontFamily || "inherit"));
  st.setProperty("--sw-act-close-bg", ac.bgColor || "transparent");
  st.setProperty("--sw-act-close-fc", ac.textColor || "#0f172a");
  st.setProperty("--sw-act-close-bc", ac.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--sw-act-close-px", `${ac.paddingX ?? 12}px`);
  st.setProperty("--sw-act-close-py", `${ac.paddingY ?? 10}px`);
  st.setProperty("--sw-act-close-r", `${ac.borderRadius ?? 10}px`);
  st.setProperty("--sw-act-close-mt", `${ac.marginTop ?? 8}px`);

  // ---- actionApply (new)
  const aa = form.actionApply || {};
  st.setProperty("--sw-act-apply-fs", `${aa.fontSize ?? 14}px`);
  st.setProperty("--sw-act-apply-fw", aa.fontWeight || "700");
  st.setProperty("--sw-act-apply-ff", normalizeFF(aa.fontFamily || "inherit"));
  st.setProperty("--sw-act-apply-bg", aa.bgColor || "#f38b4a");
  st.setProperty("--sw-act-apply-fc", aa.textColor || "#ffffff");
  st.setProperty("--sw-act-apply-bc", aa.borderColor || "transparent");
  st.setProperty("--sw-act-apply-px", `${aa.paddingX ?? 12}px`);
  st.setProperty("--sw-act-apply-py", `${aa.paddingY ?? 10}px`);
  st.setProperty("--sw-act-apply-r", `${aa.borderRadius ?? 10}px`);
  st.setProperty("--sw-act-apply-mt", `${aa.marginTop ?? 8}px`);

  // ---- modal
  const m = form.modal || {};
  const mo = m.overlay || {};
  const mp = m.panel || {};
  const mh = m.header || {};
  const mtitle = m.title || {};
  const cb = m.checkbox || {};
  const lk = m.link || {};
  const bc = m.buttonsCancel || {};
  const ba = m.buttonsApply || {};

  st.setProperty("--swm-overlay", mo.bgColor || "rgba(0,0,0,.35)");
  st.setProperty("--swm-bg", mp.bgColor || "#ffffff");
  st.setProperty("--swm-bc", mp.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--swm-r", `${mp.borderRadius ?? 16}px`);
  st.setProperty("--swm-shadow", mp.shadow || "0 8px 24px rgba(2,6,23,.12)");
  st.setProperty("--swm-pad", `${mp.padding ?? 16}px`);
  st.setProperty("--swm-div", mp.dividerColor || "rgba(148,163,184,.2)");
  st.setProperty("--swm-w", `${mp.width ?? 520}px`);

  st.setProperty("--swm-h-fs", `${mh.fontSize ?? 18}px`);
  st.setProperty("--swm-h-fw", mh.fontWeight || "800");
  st.setProperty("--swm-h-ff", normalizeFF(mh.fontFamily || "inherit"));
  st.setProperty("--swm-h-fc", mh.fontColor || "#0f172a");

  st.setProperty("--swm-title-fs", `${mtitle.fontSize ?? 20}px`);
  st.setProperty("--swm-title-fw", mtitle.fontWeight || "800");
  st.setProperty("--swm-title-ff", normalizeFF(mtitle.fontFamily || "inherit"));
  st.setProperty("--swm-title-fc", mtitle.fontColor || "#0f172a");

  st.setProperty("--swm-check-fs", `${cb.fontSize ?? 14}px`);
  st.setProperty("--swm-check-fw", cb.fontWeight || "500");
  st.setProperty("--swm-check-ff", normalizeFF(cb.fontFamily || "inherit"));
  st.setProperty("--swm-check-fc", cb.fontColor || "#0f172a");

  st.setProperty("--swm-link-fc", lk.color || "#0f172a");
  st.setProperty("--swm-link-op", lk.opacity || ".8");

  // Modal buttons
  st.setProperty("--swm-btn-fs", `${ba.fontSize ?? 14}px`);
  st.setProperty("--swm-btn-fw", ba.fontWeight || "700");
  st.setProperty("--swm-btn-ff", normalizeFF(ba.fontFamily || "inherit"));
  st.setProperty("--swm-btn-r", `${ba.radius ?? 10}px`);
  st.setProperty("--swm-btn-px", `${ba.paddingX ?? 16}px`);
  st.setProperty("--swm-btn-py", `${ba.paddingY ?? 10}px`);

  st.setProperty("--swm-cancel-fs", `${bc.fontSize ?? 14}px`);
  st.setProperty("--swm-cancel-fw", bc.fontWeight || "700");
  st.setProperty("--swm-cancel-ff", normalizeFF(bc.fontFamily || "inherit"));
  st.setProperty("--swm-cancel-r", `${bc.radius ?? 10}px`);
  st.setProperty("--swm-cancel-px", `${bc.paddingX ?? 16}px`);
  st.setProperty("--swm-cancel-py", `${bc.paddingY ?? 10}px`);
  st.setProperty("--swm-cancel-bg", bc.bg || "transparent");
  st.setProperty("--swm-cancel-fc", bc.text || "#0f172a");
  st.setProperty("--swm-cancel-bc", bc.border || "rgba(15,23,42,.2)");

  st.setProperty("--swm-apply-fs", `${ba.fontSize ?? 14}px`);
  st.setProperty("--swm-apply-fw", ba.fontWeight || "700");
  st.setProperty("--swm-apply-ff", normalizeFF(ba.fontFamily || "inherit"));
  st.setProperty("--swm-apply-r", `${ba.radius ?? 10}px`);
  st.setProperty("--swm-apply-px", `${ba.paddingX ?? 16}px`);
  st.setProperty("--swm-apply-py", `${ba.paddingY ?? 10}px`);
  st.setProperty("--swm-apply-bg", ba.bg || "#f38b4a");
  st.setProperty("--swm-apply-fc", ba.text || "#ffffff");
  st.setProperty("--swm-apply-bc", ba.border || "transparent");
}

/* ===================== NEW: Calendar style application + runtime CSS ===================== */

/** Reflect editor values to CSS vars & data-attrs on the calendar host */
function applyCalendarStyleVarsTo(el, form) {
  if (!el || !form) return;
  const st = el.style;

  // Container
  const ct = form.container || {};
  st.setProperty("--cal-ct-bg", ct.bgColor || "#ffffff");
  st.setProperty("--cal-ct-bc", ct.borderColor || "rgba(15,23,42,.2)");
  const cp = ct.padding || {};
  st.setProperty("--cal-ct-pad-t", `${cp.t ?? 8}px`);
  st.setProperty("--cal-ct-pad-r", `${cp.r ?? 8}px`);
  st.setProperty("--cal-ct-pad-b", `${cp.b ?? 8}px`);
  st.setProperty("--cal-ct-pad-l", `${cp.l ?? 8}px`);

  // Month heading
  const mh = form.monthHeading || {};
  st.setProperty("--cal-month-fs", `${mh.fontSize ?? 18}px`);
  st.setProperty("--cal-month-fw", mh.fontWeight || "800");
  st.setProperty("--cal-month-ff", normalizeFF(mh.fontFamily || "inherit"));
  st.setProperty("--cal-month-fc", mh.fontColor || "#0f172a");

  // Weekdays
  const wd = form.weekdays || {};
  st.setProperty("--cal-wd-fs", `${wd.fontSize ?? 12}px`);
  st.setProperty("--cal-wd-fw", wd.fontWeight || "600");
  st.setProperty("--cal-wd-ff", normalizeFF(wd.fontFamily || "inherit"));
  st.setProperty("--cal-wd-fc", wd.fontColor || "#0f172a");
  st.setProperty("--cal-wd-bg", wd.bgColor || "#ffffff");
  st.setProperty("--cal-wd-bc", wd.borderColor || "rgba(15,23,42,.0)");
  const wdp = wd.padding || {};
  st.setProperty("--cal-wd-pad-t", `${wdp.t ?? 6}px`);
  st.setProperty("--cal-wd-pad-r", `${wdp.r ?? 6}px`);
  st.setProperty("--cal-wd-pad-b", `${wdp.b ?? 6}px`);
  st.setProperty("--cal-wd-pad-l", `${wdp.l ?? 6}px`);
  st.setProperty("--cal-wd-td", wd.textDecoration || "");
  st.setProperty("--cal-wd-tdc", wd.textDecorationColor || "#0f172a");

  // Day base
  const d = form.day || {};
  st.setProperty("--cal-day-fs", `${d.fontSize ?? 14}px`);
  st.setProperty("--cal-day-fw", d.fontWeight || "700");
  st.setProperty("--cal-day-ff", normalizeFF(d.fontFamily || "inherit"));
  st.setProperty("--cal-day-fc", d.fontColor || "#0f172a");
  st.setProperty("--cal-day-bg", d.bgColor || "#ffffff");
  st.setProperty("--cal-day-bc", d.borderColor || "rgba(15,23,42,.12)");
  const dp = d.padding || {};
  st.setProperty("--cal-day-pad-t", `${dp.t ?? 8}px`);
  st.setProperty("--cal-day-pad-r", `${dp.r ?? 8}px`);
  st.setProperty("--cal-day-pad-b", `${dp.b ?? 8}px`);
  st.setProperty("--cal-day-pad-l", `${dp.l ?? 8}px`);
  st.setProperty("--cal-day-td", d.textDecoration || "");
  st.setProperty("--cal-day-tdc", d.textDecorationColor || "#0f172a");

  // Day hover
  const dh = form.dayHover || {};
  st.setProperty("--cal-dayh-bg", dh.bgColor || "#f3f4f6");
  st.setProperty("--cal-dayh-fc", dh.fontColor || "#0f172a");
  st.setProperty("--cal-dayh-fw", dh.fontWeight || "700");
  st.setProperty("--cal-dayh-ff", normalizeFF(dh.fontFamily || "inherit"));
  st.setProperty("--cal-dayh-fs", `${dh.fontSize ?? 14}px`);

  // Special states
  const pd = form.pastDisabled || {};
  st.setProperty("--cal-past-fc", pd.fontColor || "#9ca3af");
  st.setProperty("--cal-past-bg", pd.bgColor || "#f9fafb");

  const na = form.notAvailable || {};
  st.setProperty("--cal-na-fc", na.fontColor || "#9ca3af");
  st.setProperty("--cal-na-bg", na.bgColor || "#f3f4f6");

  const fr = form.fromDay || {};
  st.setProperty("--cal-from-fc", fr.fontColor || "#ffffff");
  st.setProperty("--cal-from-bg", fr.bgColor || "#f38b4a");
  const frr = fr.borderRadius || {};
  st.setProperty("--cal-from-r-t", `${frr.t ?? 8}px`);
  st.setProperty("--cal-from-r-r", `${frr.r ?? 8}px`);
  st.setProperty("--cal-from-r-b", `${frr.b ?? 8}px`);
  st.setProperty("--cal-from-r-l", `${frr.l ?? 8}px`);

  const to = form.toDay || {};
  st.setProperty("--cal-to-fc", to.fontColor || "#ffffff");
  st.setProperty("--cal-to-bg", to.bgColor || "#f38b4a");
  const tor = to.borderRadius || {};
  st.setProperty("--cal-to-r-t", `${tor.t ?? 8}px`);
  st.setProperty("--cal-to-r-r", `${tor.r ?? 8}px`);
  st.setProperty("--cal-to-r-b", `${tor.b ?? 8}px`);
  st.setProperty("--cal-to-r-l", `${tor.l ?? 8}px`);

  const ib = form.inBetween || {};
  st.setProperty("--cal-mid-bg", ib.bgColor || "#fde7d8");
  const ibr = ib.borderRadius || {};
  st.setProperty("--cal-mid-r-t", `${ibr.t ?? 8}px`);
  st.setProperty("--cal-mid-r-r", `${ibr.r ?? 8}px`);
  st.setProperty("--cal-mid-r-b", `${ibr.b ?? 8}px`);
  st.setProperty("--cal-mid-r-l", `${ibr.l ?? 8}px`);

  // ::after badges (check-in / checkout)
  const cia = form.checkinAfter || {};
  st.setProperty("--cal-cia-bg", cia.bgColor || "#00000000");
  st.setProperty("--cal-cia-fc", cia.fontColor || "#0f172a");
  st.setProperty("--cal-cia-bc", cia.borderColor || "rgba(15,23,42,.0)");
  const ciap = cia.padding || {};
  st.setProperty("--cal-cia-pad-t", `${ciap.t ?? 0}px`);
  st.setProperty("--cal-cia-pad-r", `${ciap.r ?? 0}px`);
  st.setProperty("--cal-cia-pad-b", `${ciap.b ?? 0}px`);
  st.setProperty("--cal-cia-pad-l", `${ciap.l ?? 0}px`);
  st.setProperty("--cal-cia-td", cia.textDecoration || "");
  st.setProperty("--cal-cia-tdc", cia.textDecorationColor || "#0f172a");

  const coa = form.checkoutAfter || {};
  st.setProperty("--cal-coa-bg", coa.bgColor || "#00000000");
  st.setProperty("--cal-coa-fc", coa.fontColor || "#0f172a");
  st.setProperty("--cal-coa-bc", coa.borderColor || "rgba(15,23,42,.0)");
  const coap = coa.padding || {};
  st.setProperty("--cal-coa-pad-t", `${coap.t ?? 0}px`);
  st.setProperty("--cal-coa-pad-r", `${coap.r ?? 0}px`);
  st.setProperty("--cal-coa-pad-b", `${coap.b ?? 0}px`);
  st.setProperty("--cal-coa-pad-l", `${coap.l ?? 0}px`);
  st.setProperty("--cal-coa-td", coa.textDecoration || "");
  st.setProperty("--cal-coa-tdc", coa.textDecorationColor || "#0f172a");

  // Nav buttons + icon
  const nb = form.navButton || {};
  st.setProperty("--cal-nav-bc", nb.borderColor || "rgba(15,23,42,.12)");
  st.setProperty("--cal-nav-w", `${nb.width ?? 36}px`);
  st.setProperty("--cal-nav-h", `${nb.height ?? 36}px`);
  const nbr = nb.radius || {};
  st.setProperty("--cal-nav-r-t", `${nbr.t ?? 10}px`);
  st.setProperty("--cal-nav-r-r", `${nbr.r ?? 10}px`);
  st.setProperty("--cal-nav-r-b", `${nbr.b ?? 10}px`);
  st.setProperty("--cal-nav-r-l", `${nbr.l ?? 10}px`);
  st.setProperty("--cal-nav-ic", nb.iconColor || "#0f172a");

  // Dropdown container
  const dd = form.dropdown || {};
  st.setProperty("--cal-dropdown-bg", dd.bgColor || "#ffffff");
  st.setProperty("--cal-dropdown-bc", dd.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--cal-dropdown-r", `${dd.borderRadius ?? 12}px`);
  st.setProperty("--cal-dropdown-shadow", dd.boxShadow || "0 10px 25px rgba(0,0,0,.1)");
  st.setProperty("--cal-dropdown-z", String(dd.zIndex ?? 9999));
  st.setProperty("--cal-dropdown-maxw", `${dd.maxWidth ?? 1200}px`);
  const ddp = dd.padding || {};
  st.setProperty("--cal-dropdown-pad-t", `${ddp.t ?? 16}px`);
  st.setProperty("--cal-dropdown-pad-r", `${ddp.r ?? 16}px`);
  st.setProperty("--cal-dropdown-pad-b", `${ddp.b ?? 16}px`);
  st.setProperty("--cal-dropdown-pad-l", `${ddp.l ?? 16}px`);

  // Input field (trigger)
  const inp = form.input || {};
  st.setProperty("--cal-input-fs", `${inp.fontSize ?? 14}px`);
  st.setProperty("--cal-input-fw", inp.fontWeight || "500");
  st.setProperty("--cal-input-ff", normalizeFF(inp.fontFamily || "inherit"));
  st.setProperty("--cal-input-fc", inp.fontColor || "#0f172a");
  st.setProperty("--cal-input-bg", inp.bgColor || "#ffffff");
  st.setProperty("--cal-input-bc", inp.borderColor || "rgba(15,23,42,.2)");
  st.setProperty("--cal-input-r", `${inp.borderRadius ?? 8}px`);
  const inpp = inp.padding || {};
  st.setProperty("--cal-input-pad-t", `${inpp.t ?? 10}px`);
  st.setProperty("--cal-input-pad-r", `${inpp.r ?? 12}px`);
  st.setProperty("--cal-input-pad-b", `${inpp.b ?? 10}px`);
  st.setProperty("--cal-input-pad-l", `${inpp.l ?? 12}px`);
  st.setProperty("--cal-input-placeholder-fc", inp.placeholderColor || "#9ca3af");

  // icon variant as data-attr for your runtime (arrow|chevron|chevron-double|triangle)
  const iconVar = (form.icon?.variant || "arrow").toLowerCase();
  el.setAttribute("data-cal-icon", iconVar);
}

/* ======================================================================================== */

export default function Widget({
  theme = "light",
  maxGuests = 16,
  apiOrigin = "https://api.nexus.design",
  accountIdProp = "",
}) {
  const [stylesLoaded, setStylesLoaded] = useState(false);

  const palette = useMemo(() => {
    const dark = theme === "dark";
    return {
      bg: dark ? "#0f172a" : "#ffffff",
      fg: dark ? "#e2e8f0" : "#0f172a",
      border: dark ? "rgba(226,232,240,.2)" : "rgba(15,23,42,.2)",
      soft: dark ? "rgba(148,163,184,.2)" : "rgba(15,23,42,.06)",
      shadow: dark ? "0 6px 22px rgba(0,0,0,.35)" : "0 8px 24px rgba(2,6,23,.12)",
    };
  }, [theme]);

  // Read data-is-url-changed attribute
  const isUrlChangedMode = useMemo(() => {
    const hostEl = getHostEl();
    const dataIsUrlChanged = hostEl?.getAttribute("data-is-url-changed");
    return dataIsUrlChanged === "true" || dataIsUrlChanged === "1";
  }, []);

  const getParam = (name) => new URLSearchParams(window.location.search).get(name);

  // setParams now respects data-is-url-changed mode
  const setParams = (obj, forceUpdate = false) => {
    const url = new URL(window.location.href);
    const sp = url.searchParams;

    Object.entries(obj).forEach(([k, v]) => {
      if (v === undefined || v === null || v === "" || String(v) === "NaN") {
        sp.delete(k);
      } else {
        sp.set(k, String(v));
      }
    });

    ["adults", "children", "infants", "pets", "guests"].forEach((k) => {
      const val = sp.get(k);
      if (val === "NaN" || val === "" || val === null) sp.delete(k);
    });

    history.replaceState({}, "", url.toString());

    // If data-is-url-changed="true" and not forced, don't dispatch locationchange event
    // If data-is-url-changed="false" or forceUpdate=true, dispatch event (triggers search)
    if (!isUrlChangedMode || forceUpdate) {
      window.dispatchEvent(new Event("locationchange"));
    }
  };

  const pi = (v, fb) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : fb;
  };
  const [adults, setAdults] = useState(() => Math.max(1, pi(getParam("adults"), 1)));
  const [children, setChildren] = useState(() => Math.max(0, pi(getParam("children"), 0)));
  const [infants, setInfants] = useState(() => Math.max(0, pi(getParam("infants"), 0)));
  const [pets, setPets] = useState(() => Math.max(0, pi(getParam("pets"), 0)));
  const guests = Math.max(1, adults + children);

  // Location state
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);

  // Store fetched form configuration for button order
  const [form, setForm] = useState(null);

  useEffect(() => {
    // Only update guest and city params - preserve other params like flexibilityDays, dates, filters, etc.
    const currentParams = new URLSearchParams(window.location.search);
    const preservedParams = {};

    // Preserve existing params that shouldn't be touched
    const paramsToPreserve = ['startDate', 'endDate', 'flexibilityDays', 'amenities', 'tags', 'beds', 'bedrooms', 'bathrooms', 'categories', 'type', 'state', 'country', 'complex', 'is_featured', 'price_min', 'price_max', 'sort', 'page', 'per_page'];
    paramsToPreserve.forEach(param => {
      const value = currentParams.get(param);
      if (value !== null) {
        preservedParams[param] = value;
      }
    });

    // Update only guest-related and city params
    setParams({
      ...preservedParams,
      adults,
      children,
      infants,
      pets,
      guests,
      city: selectedCity
    });
  }, [adults, children, infants, pets, guests, selectedCity]);

  // Inject critical styles on mount
  useEffect(() => {
    injectWidgetStyles();
  }, []);

  const wrap = {
    fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif",
    background: "var(--sw-container-bg, " + palette.bg + ")",
    color: palette.fg,
    padding: "var(--sw-container-pad, 12px)",
    borderRadius: "var(--sw-container-r, 12px)",
    border: "1px solid var(--sw-container-bc, " + palette.border + ")",
    gap: "12px",
  };

  const [filtersOpen, setFiltersOpen] = useState(false);

  const filterBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--sw-filter-gap, 8px)",
    border: `1px solid var(--sw-filter-bc, ${palette.border})`,
    borderRadius: "var(--sw-filter-r, 10px)",
    padding: "var(--sw-filter-py, 10px) var(--sw-filter-px, 12px)",
    background: "var(--sw-filter-bg, transparent)",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    fontFamily: "var(--sw-filter-ff, inherit)",
    fontSize: "var(--sw-filter-fs, 14px)",
    fontWeight: "var(--sw-filter-fw, 600)",
    color: "var(--sw-filter-fc, inherit)",
    minWidth: "var(--sw-filter-minw, 80px)",
    height: "var(--sw-filter-h, 44px)",
    boxSizing: "border-box",
  };

  /* 🔹 OPTIMIZED: Single fetch for both search & calendar widget styles + inject all CSS once */
  useEffect(() => {
    // Inject CSS first (synchronous, before any fetch)
    ensureSearchWidgetResponsiveCSS();
    ensureCalendarRuntimeCSS();
    ensureSearchCalendarInputCSS();

    // Single account fetch for both widget styles
    (async () => {
      try {
        const host = getHostEl();
        const calHost = getCalendarHostEl();

        if (!host) return;

        // Resolve account ID once
        const urlAcct = new URLSearchParams(window.location.search).get("account");
        const domAcct =
          host.getAttribute("data-account-id") ||
          host.getAttribute("data-account") ||
          calHost?.getAttribute("data-account-id") ||
          calHost?.getAttribute("data-account") ||
          "";
        const accountId = urlAcct || domAcct;
        if (!accountId) {
          // Add ready class to HOST element to show widget
          host?.classList.add('nexus-styles-ready');
          setStylesLoaded(true); // Show widget with default styles
          return;
        }

        // Read styling from data attributes (self-contained!)
        const hostEl = getHostEl();
        const get = (attr) => hostEl?.getAttribute(attr);
        const getInt = (attr, fallback) => parseInt(get(attr)) || fallback;
        const getBool = (attr, fallback) => {
          const val = get(attr);
          return val === null ? fallback : (val === 'true' || val === '1');
        };

        // Build style object from data attributes
        // MUST match SearchWidgetStyleLayer.jsx generateEmbedCode() exactly!
        const styleObj = {
          widgetContainer: {
            padding: getInt('data-container-padding', 12),
            borderRadius: getInt('data-container-radius', 12),
            borderColor: get('data-container-border-color') || 'rgba(15,23,42,.2)',
            bgColor: get('data-container-bg-color') || 'transparent',
            // FIX: buttonOrder is sent as comma-separated string, not JSON
            buttonOrder: get('data-button-order') ? get('data-button-order').split(',') : ['location', 'calendar', 'guests', 'filter', 'searchButton'],
          },
          pill: {
            paddingX: getInt('data-pill-padding-x', 12),
            paddingY: getInt('data-pill-padding-y', 10),
            borderRadius: getInt('data-pill-border-radius', 10),
            borderColor: get('data-pill-border-color') || 'rgba(15,23,42,.2)',
            bgColor: get('data-pill-bg-color') || 'transparent',
            fontSize: getInt('data-pill-font-size', 14),
            fontWeight: get('data-pill-font-weight') || '600',
            fontFamily: get('data-pill-font-family') || 'inherit', // ADDED
            fontColor: get('data-pill-font-color') || '#0f172a',
            gap: getInt('data-pill-gap', 8),
            minWidth: getInt('data-pill-min-width', 150),
            minWidthUnit: get('data-pill-min-width-unit') || 'px', // ADDED
            icon: {
              mode: get('data-guest-icon-mode') || 'fa',
              faFamily: get('data-guest-icon-family') || 'fa-solid',
              faIcon: get('data-guest-icon-name') || 'fa-user',
              size: getInt('data-guest-icon-size', 16),
              color: get('data-guest-icon-color') || '#0f172a',
              customSvg: get('data-guest-icon-svg') || '',
              show: getBool('data-guest-icon-show', true),
            },
          },
          location: {
            icon: {
              mode: get('data-location-icon-mode') || 'fa',
              faFamily: get('data-location-icon-family') || 'fa-solid',
              faIcon: get('data-location-icon-name') || 'fa-location-dot',
              size: getInt('data-location-icon-size', 16),
              color: get('data-location-icon-color') || '#0f172a',
              customSvg: get('data-location-icon-svg') || '',
              show: getBool('data-location-icon-show', true),
            },
          },
          calendarBtn: {
            paddingX: getInt('data-cal-padding-x', 12),
            paddingY: getInt('data-cal-padding-y', 10),
            borderRadius: getInt('data-cal-radius', 10),
            borderColor: get('data-cal-border-color') || 'rgba(15,23,42,.2)',
            bgColor: get('data-cal-bg-color') || 'transparent',
            fontSize: getInt('data-cal-font-size', 14),
            fontWeight: get('data-cal-font-weight') || '500',
            fontFamily: get('data-cal-font-family') || 'inherit', // ADDED
            fontColor: get('data-cal-font-color') || '#0f172a',
            placeholderColor: get('data-cal-placeholder-color') || '#0f172a',
            minWidth: getInt('data-cal-min-width', 150),
            minWidthUnit: get('data-cal-min-width-unit') || 'px', // ADDED
            icon: {
              mode: get('data-calendar-icon-mode') || 'fa',
              faFamily: get('data-calendar-icon-family') || 'fa-solid',
              faIcon: get('data-calendar-icon-name') || 'fa-calendar',
              size: getInt('data-calendar-icon-size', 16),
              color: get('data-calendar-icon-color') || '#0f172a',
              customSvg: get('data-calendar-icon-svg') || '',
              show: getBool('data-calendar-icon-show', true),
            },
          },
          filter: {
            paddingX: getInt('data-filter-padding-x', 12),
            paddingY: getInt('data-filter-padding-y', 10),
            borderRadius: getInt('data-filter-border-radius', 10),
            borderColor: get('data-filter-border-color') || 'rgba(15,23,42,.2)',
            bgColor: get('data-filter-bg-color') || 'transparent',
            fontSize: getInt('data-filter-font-size', 14), // ADDED
            fontWeight: get('data-filter-font-weight') || '600', // ADDED
            fontFamily: get('data-filter-font-family') || 'inherit', // ADDED
            fontColor: get('data-filter-font-color') || '#0f172a', // ADDED
            gap: getInt('data-filter-gap', 8), // ADDED
            minWidth: getInt('data-filter-min-width', 80), // ADDED
            minWidthUnit: get('data-filter-min-width-unit') || 'px', // ADDED
            icon: {
              mode: get('data-filter-icon-mode') || 'fa',
              faFamily: get('data-filter-icon-family') || 'fa-solid',
              faIcon: get('data-filter-icon-name') || 'fa-sliders',
              size: getInt('data-filter-icon-size', 16),
              color: get('data-filter-icon-color') || '#0f172a',
              customSvg: get('data-filter-icon-svg') || '',
              show: getBool('data-filter-icon-show', true),
            },
          },
          searchButton: {
            paddingX: getInt('data-search-button-padding-x', 16),
            paddingY: getInt('data-search-button-padding-y', 10),
            borderRadius: getInt('data-search-button-border-radius', 10),
            borderColor: get('data-search-button-border-color') || 'transparent',
            bgColor: get('data-search-button-bg-color') || '#f38b4a',
            fontColor: get('data-search-button-font-color') || '#ffffff',
            fontSize: getInt('data-search-button-font-size', 14),
            fontWeight: get('data-search-button-font-weight') || '600',
            fontFamily: get('data-search-button-font-family') || 'inherit',
            minWidth: getInt('data-search-button-min-width', 90),
            minWidthUnit: get('data-search-button-min-width-unit') || 'px',
            icon: { mode: 'fa', faFamily: 'fa-solid', faIcon: 'fa-search', size: 16, color: '#ffffff', show: true },
          },
          panel: {
            bgColor: get('data-panel-bg-color') || '#ffffff',
            borderColor: get('data-panel-border-color') || 'rgba(15,23,42,.2)',
            borderRadius: getInt('data-panel-border-radius', 12),
            padding: getInt('data-panel-padding', 12),
            width: getInt('data-panel-width', 300),
            shadow: get('data-panel-shadow') || '0 8px 24px rgba(2,6,23,.12)',
          },
        };

        logger.log('[SearchWidget] Loaded styling from data attributes:', styleObj);

        // Always load Font Awesome for icons (default mode)
        ensureFontAwesome(true);

        // Apply search widget styles
        setForm(styleObj);
        applySearchStyleVarsTo(host, styleObj);

        // Mark styles as loaded - add ready class to HOST element to show widget
        host?.classList.add('nexus-styles-ready');
        setStylesLoaded(true);

        // Fetch account data for cities and calendar styles (NO CACHING - direct API call)
        logger.log('[SearchWidget] Fetching account data directly from API (no cache)');
        const accountRes = await fetch(`${apiOrigin.replace(/\/+$/, "")}/api/accounts/${accountId}`);
        if (!accountRes.ok) {
          logger.warn('[SearchWidget] Failed to fetch account data:', accountRes.status);
          return;
        }

        const json = await accountRes.json();
        if (!json) return;

        // Load cities from selected_cities
        if (json?.selected_cities && Array.isArray(json.selected_cities)) {
          const citiesList = json.selected_cities.map(item => {
            // Handle nested format: { city: {...}, order: 0 }
            if (item.city && typeof item.city === 'object') {
              return item.city;
            }
            return item;
          });
          setCities(citiesList);
          logger.log('[SearchWidget] Loaded cities from API:', citiesList.length);
        }

        // Apply calendar widget styles if calendar host exists (from API for embedded calendar)
        // Note: Calendar is a separate widget component, so it can have its own styles from API
        if (calHost) {
          const calStyle = parseMaybe(json?.calendar_widget_style);
          if (calStyle) {
            applyCalendarStyleVarsTo(calHost, calStyle);
            logger.log('[SearchWidget] Applied calendar styles from API');
          }
        }
      } catch (err) {
        logger.warn('[SearchWidget] Failed to load styles:', err);
        // Load FA by default on error
        ensureFontAwesome(true);
        // Add ready class to HOST element to show widget even on error
        getHostEl()?.classList.add('nexus-styles-ready');
        setStylesLoaded(true); // Show widget with default styles
      }
    })();
  }, [apiOrigin]);

  // Read data-button attribute to determine if search button should be shown
  const [showSearchButton, setShowSearchButton] = useState(() => {
    const hostEl = getHostEl();
    const dataButton = hostEl?.getAttribute("data-button");
    logger.log("[SearchWidget] Initial data-button:", dataButton);
    return dataButton === "true" || dataButton === "1";
  });

  // Watch for data-button attribute changes
  useEffect(() => {
    const hostEl = getHostEl();
    if (!hostEl) {
      logger.log("[SearchWidget] No host element found!");
      return;
    }

    logger.log("[SearchWidget] Setting up MutationObserver for data-button");
    const observer = new MutationObserver(() => {
      const dataButton = hostEl.getAttribute("data-button");
      logger.log("[SearchWidget] data-button changed to:", dataButton);
      setShowSearchButton(dataButton === "true" || dataButton === "1");
    });

    observer.observe(hostEl, { attributes: true, attributeFilter: ["data-button"] });
    return () => observer.disconnect();
  }, []);

  // Read data-button-url attribute
  const searchButtonUrl = useMemo(() => {
    const hostEl = getHostEl();
    return hostEl?.getAttribute("data-button-url") || "";
  }, []);

  // Search button click handler
  const handleSearchButtonClick = () => {
    if (searchButtonUrl) {
      // If data-button-url has value, navigate to that URL with all current URL parameters
      const currentParams = new URLSearchParams(window.location.search);
      const targetUrl = new URL(searchButtonUrl, window.location.origin);

      // Preserve all existing URL parameters (including flexibilityDays, dates, filters, etc.)
      for (const [key, value] of currentParams.entries()) {
        if (!targetUrl.searchParams.has(key)) {
          targetUrl.searchParams.set(key, value);
        }
      }

      window.location.href = targetUrl.toString();
    } else {
      // If data-button-url is empty, trigger search by dispatching locationchange event
      // This forces an update even when data-is-url-changed="true"
      window.dispatchEvent(new Event("locationchange"));
    }
  };

  // Button components mapping (without wrapper divs - handled in render)
  const buttonComponents = useMemo(() => ({
    location: (
      <LocationDropdown
        key="location"
        palette={palette}
        cities={cities}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        fullWidth={true}
      />
    ),
    calendar: (
      <CalendarPane key="calendar" palette={palette} fullWidth={true} />
    ),
    guests: (
      <GuestsDropdown
        key="guests"
        palette={palette}
        maxGuests={maxGuests}
        adults={adults}
        children={children}
        infants={infants}
        pets={pets}
        setAdults={setAdults}
        setChildren={setChildren}
        setInfants={setInfants}
        setPets={setPets}
        fullWidth={true}
      />
    ),
    filter: (
      <button key="filter" type="button" data-button-type="filter" style={{ ...filterBtn, width: "100%" }} onClick={() => setFiltersOpen(true)}>
        <FilterIcon />
        <span>{getHostEl()?.getAttribute("data-filter-label") || "Filter"}</span>
      </button>
    ),
    searchButton: (
      <button
        key="searchButton"
        type="button"
        data-button-type="search"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          width: "100%",
          border: `1px solid var(--sw-searchbtn-bc, transparent)`,
          borderRadius: "var(--sw-searchbtn-r, 10px)",
          padding: "var(--sw-searchbtn-py, 10px) var(--sw-searchbtn-px, 16px)",
          background: "var(--sw-searchbtn-bg, #f38b4a)",
          cursor: "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
          fontFamily: "var(--sw-searchbtn-ff, inherit)",
          fontSize: "var(--sw-searchbtn-fs, 14px)",
          fontWeight: "var(--sw-searchbtn-fw, 600)",
          color: "var(--sw-searchbtn-fc, #ffffff)",
          minWidth: "var(--sw-searchbtn-minw, 90px)",
          height: "var(--sw-searchbtn-h, 44px)",
          boxSizing: "border-box",
        }}
        onClick={handleSearchButtonClick}
      >
        <SearchButtonIcon />
        <span>{getHostEl()?.getAttribute("data-search-button-label") || "Search"}</span>
      </button>
    ),
  }), [palette, maxGuests, adults, children, infants, pets, filterBtn, searchButtonUrl, cities, selectedCity]);

  // Get button order from form or use default, conditionally include search button and location
  let buttonOrder = form?.widgetContainer?.buttonOrder || ["location", "calendar", "guests", "filter", "searchButton"];

  // Remove mapToggle if it exists in saved settings (deprecated)
  if (buttonOrder.includes("mapToggle")) {
    buttonOrder = buttonOrder.filter(key => key !== "mapToggle");
  }

  // Migration: ensure calendar is in the order (for old settings that don't have it)
  if (!buttonOrder.includes("calendar")) {
    buttonOrder = ["calendar", ...buttonOrder];
  }

  // Migration: ensure location is in the order (for old settings that don't have it)
  if (!buttonOrder.includes("location")) {
    // Insert location before calendar if calendar exists
    const calendarIndex = buttonOrder.indexOf("calendar");
    if (calendarIndex !== -1) {
      buttonOrder = [...buttonOrder.slice(0, calendarIndex), "location", ...buttonOrder.slice(calendarIndex)];
    } else {
      buttonOrder = ["location", ...buttonOrder];
    }
  }

  logger.log("[SearchWidget] showSearchButton:", showSearchButton, "buttonOrder:", buttonOrder);

  // If showSearchButton is true and searchButton is not in the order, append it
  if (showSearchButton && !buttonOrder.includes("searchButton")) {
    buttonOrder = [...buttonOrder, "searchButton"];
    logger.log("[SearchWidget] Added searchButton to order:", buttonOrder);
  }
  // If showSearchButton is false, remove searchButton from the order
  if (!showSearchButton && buttonOrder.includes("searchButton")) {
    buttonOrder = buttonOrder.filter(key => key !== "searchButton");
    logger.log("[SearchWidget] Removed searchButton from order:", buttonOrder);
  }

  const orderedButtons = buttonOrder.map(key => ({ key, component: buttonComponents[key] })).filter(item => item.component);
  logger.log("[SearchWidget] orderedButtons count:", orderedButtons.length);

  // Calculate bootstrap-like columns based on total buttons (including calendar)
  const totalItems = orderedButtons.length;

  // Define which buttons are inputs vs buttons for grid sizing
  const inputTypes = ['location', 'calendar', 'guests'];

  // Widget uses CSS class to control visibility - prevents flash of unstyled content
  // The CSS rule .nexus-search-widget:not(.nexus-styles-ready) hides the widget
  // Adding .nexus-styles-ready class shows it with a smooth transition

  return (
    <div
      className={`nexus-search-widget ${stylesLoaded ? 'nexus-styles-ready' : ''}`}
      data-nexus-search
      style={wrap}
    >
      {/* All buttons including calendar - inputs get 2fr, buttons get 1fr */}
      {orderedButtons.map((item, idx) => (
        <div key={idx} data-type={inputTypes.includes(item.key) ? 'input' : 'button'}>
          {item.component}
        </div>
      ))}

      {filtersOpen && (
        <FilterModal
          palette={palette}
          accountId={accountIdProp}
          onApply={(params) => {
            // Preserve existing params that FilterModal doesn't handle
            const currentParams = new URLSearchParams(window.location.search);
            const preservedParams = {};
            const paramsToPreserve = ['startDate', 'endDate', 'flexibilityDays', 'adults', 'children', 'infants', 'pets', 'guests', 'city', 'categories', 'type', 'state', 'country', 'complex', 'is_featured', 'price_min', 'price_max', 'sort', 'page', 'per_page'];

            paramsToPreserve.forEach(param => {
              const value = currentParams.get(param);
              if (value !== null && !(param in params)) {
                preservedParams[param] = value;
              }
            });

            setParams({ ...preservedParams, ...params });
            setFiltersOpen(false);
          }}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </div>
  );
}
